import { Link, Navigate } from 'react-router-dom';
import {
  ArrowRight,
  CheckCircle2,
  Crown,
  Eye,
  Sparkles,
  Store,
  TrendingUp,
  Users,
} from 'lucide-react';
import SEOHead from '../components/SEOHead';
import BrandLogo from '../components/BrandLogo';
import { Button } from '../components/ui/button';
import { SUPPLIERS_BETA_ENABLED } from '../lib/featureFlags';
import { SUPPLIER_PLAN_OPTIONS, getSupplierPlanMeta } from '../lib/suppliers';

const BENEFITS = [
  {
    title: 'Presenca profissional',
    description: 'Seu perfil vira uma vitrine editavel dentro do Virei Noiva, com identidade, categoria, cidade e contato direto.',
    icon: Store,
  },
  {
    title: 'Mais descoberta',
    description: 'Fornecedores aprovados entram no diretorio e podem ganhar distribuicao extra em vitrines premium e na home.',
    icon: Eye,
  },
  {
    title: 'Base para vender anuncio',
    description: 'A hierarquia entre Free, Premium e Premium Plus cria um produto de visibilidade que ja pode ser testado comercialmente.',
    icon: TrendingUp,
  },
];

const POSITIONING_POINTS = [
  'Nao e apenas cadastro: e um espaco de divulgacao dentro de um produto para casais.',
  'O fornecedor entra para ganhar descoberta, reputacao e mais chances de contato.',
  'O plano pago nao vende software, vende distribuicao e percepcao premium.',
];

export default function SupplierMarketingPage() {
  if (!SUPPLIERS_BETA_ENABLED) {
    return <Navigate to="/" replace />;
  }

  return (
    <div className="min-h-screen bg-[#F6F1EF] text-[#1C1C1C]">
      <SEOHead
        title="Trabalha com casamentos? | Virei Noiva"
        description="Cadastre seu perfil de fornecedor e apareca para casais dentro do Virei Noiva."
        noindex
      />

      <header className="border-b border-[#E8B7B1]/35 bg-white/90 backdrop-blur">
        <div className="container-wedding flex h-20 items-center justify-between gap-4">
          <Link to="/" className="inline-flex items-center">
            <BrandLogo size="lg" showTagline />
          </Link>
          <div className="flex gap-3">
            <Link to="/fornecedor/login">
              <Button variant="outline" className="rounded-full border-[#E8B7B1] bg-white text-[#9A6B65] hover:bg-[#F6F1EF]">
                Entrar
              </Button>
            </Link>
            <Link to="/fornecedor/cadastro">
              <Button className="rounded-full bg-[#E8B7B1] text-white hover:bg-[#DDA39D]">
                Criar perfil
              </Button>
            </Link>
          </div>
        </div>
      </header>

      <main className="container-wedding py-14">
        <section className="grid gap-8 rounded-[2rem] border border-[#E8B7B1]/35 bg-white p-8 shadow-[0_28px_70px_-34px_rgba(28,28,28,0.3)] lg:grid-cols-[1.05fr_0.95fr] lg:p-12">
          <div>
            <p className="inline-flex rounded-full border border-[#E8B7B1]/45 bg-[#FDF6F3] px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.22em] text-[#C8A97E]">
              Area de fornecedores
            </p>
            <h1 className="mt-5 text-4xl leading-tight md:text-6xl" style={{ fontFamily: 'Playfair Display, serif' }}>
              Seu proximo canal de divulgacao para casais comeca aqui.
            </h1>
            <p className="mt-5 max-w-2xl text-lg leading-relaxed text-[#4B403D]">
              O Virei Noiva esta abrindo um novo espaco para fornecedores de casamento criarem perfil, ganharem visibilidade e testarem um produto de anuncio dentro de um ecossistema pensado para casais.
            </p>

            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/fornecedor/cadastro">
                <Button className="h-12 rounded-full bg-[#1C1C1C] px-6 text-white hover:bg-[#333333]">
                  Criar meu perfil agora
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
              <Link to="/fornecedores">
                <Button variant="outline" className="h-12 rounded-full border-[#E8B7B1] bg-white px-6 text-[#9A6B65] hover:bg-[#F6F1EF]">
                  Ver vitrine de fornecedores
                </Button>
              </Link>
            </div>

            <div className="mt-8 grid gap-3 sm:grid-cols-3">
              <div className="rounded-2xl border border-[#E8B7B1]/35 bg-[#FDF6F3] p-4">
                <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[#C8A97E]">Free</p>
                <p className="mt-2 text-lg font-semibold text-[#1C1C1C]">Entrada no diretorio</p>
              </div>
              <div className="rounded-2xl border border-[#D9BA8D]/45 bg-[#FFF5E9] p-4">
                <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[#B9854A]">Premium</p>
                <p className="mt-2 text-lg font-semibold text-[#1C1C1C]">Mais visibilidade</p>
              </div>
              <div className="rounded-2xl border border-[#1C1C1C]/15 bg-[#F6F1EF] p-4">
                <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-[#1C1C1C]">Premium Plus</p>
                <p className="mt-2 text-lg font-semibold text-[#1C1C1C]">Maxima distribuicao</p>
              </div>
            </div>
          </div>

          <div className="grid gap-4">
            {BENEFITS.map((item) => {
              const Icon = item.icon;
              return (
                <article key={item.title} className="rounded-[1.4rem] border border-[#E8B7B1]/35 bg-[#FFFDFC] p-5">
                  <Icon className="h-8 w-8 text-[#C8A97E]" />
                  <h2 className="mt-4 text-2xl" style={{ fontFamily: 'Playfair Display, serif' }}>{item.title}</h2>
                  <p className="mt-2 text-sm leading-relaxed text-[#4B403D]">
                    {item.description}
                  </p>
                </article>
              );
            })}
          </div>
        </section>

        <section className="mt-12 grid gap-4 md:grid-cols-3">
          {POSITIONING_POINTS.map((item) => (
            <article key={item} className="rounded-[1.4rem] border border-[#E8B7B1]/35 bg-white p-5">
              <CheckCircle2 className="h-6 w-6 text-[#C8A97E]" />
              <p className="mt-3 text-sm leading-relaxed text-[#4B403D]">{item}</p>
            </article>
          ))}
        </section>

        <section className="mt-14">
          <div className="mb-6 max-w-3xl">
            <p className="text-xs font-semibold uppercase tracking-[0.22em] text-[#C8A97E]">Oferta comercial</p>
            <h2 className="mt-3 text-4xl" style={{ fontFamily: 'Playfair Display, serif' }}>
              Tres niveis de anuncio para comecar a vender visibilidade
            </h2>
            <p className="mt-3 text-[#4B403D]">
              A logica comercial do produto nao e cobrar pelo cadastro. E cobrar por distribuicao, prioridade e presenca premium dentro da vitrine de fornecedores.
            </p>
          </div>

          <div className="grid gap-5 xl:grid-cols-3">
            {SUPPLIER_PLAN_OPTIONS.map((plan) => {
              const planMeta = getSupplierPlanMeta(plan.id);
              const isMiddle = plan.id === 'premium';
              const isTop = plan.id === 'premium_plus';

              return (
                <article
                  key={plan.id}
                  className={`rounded-[1.8rem] border p-6 ${planMeta.cardClass} ${isMiddle ? 'xl:-translate-y-2' : ''}`}
                >
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-[#C8A97E]">{plan.eyebrow}</p>
                      <h3 className="mt-2 text-3xl text-[#1C1C1C]" style={{ fontFamily: 'Playfair Display, serif' }}>
                        {plan.name}
                      </h3>
                    </div>
                    {isTop ? (
                      <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-[#1C1C1C] text-white">
                        <Crown className="h-5 w-5" />
                      </span>
                    ) : isMiddle ? (
                      <span className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-[#C8A97E] text-white">
                        <Sparkles className="h-5 w-5" />
                      </span>
                    ) : null}
                  </div>

                  <p className="mt-4 text-sm leading-relaxed text-[#4B403D]">{plan.description}</p>

                  <ul className="mt-6 space-y-3 text-sm text-[#333333]">
                    {plan.features.map((feature) => (
                      <li key={feature} className="flex items-start gap-2">
                        <CheckCircle2 className="mt-0.5 h-4 w-4 text-[#C8A97E]" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>

                  <div className="mt-8">
                    <Link to="/fornecedor/cadastro">
                      <Button className={`${isTop ? 'bg-[#1C1C1C] hover:bg-[#333333]' : isMiddle ? 'bg-[#C8A97E] hover:bg-[#B69669]' : 'bg-[#E8B7B1] hover:bg-[#DDA39D]'} h-11 w-full rounded-full text-white`}>
                        {plan.id === 'free' ? 'Entrar na vitrine' : plan.id === 'premium' ? 'Quero mais visibilidade' : 'Quero maxima distribuicao'}
                      </Button>
                    </Link>
                  </div>
                </article>
              );
            })}
          </div>
        </section>

        <section className="mt-14 rounded-[2rem] border border-[#E8B7B1]/35 bg-white p-8 shadow-[0_20px_50px_-30px_rgba(28,28,28,0.28)]">
          <div className="grid gap-6 lg:grid-cols-[1fr_0.9fr]">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.22em] text-[#C8A97E]">Como vendemos isso</p>
              <h2 className="mt-3 text-4xl" style={{ fontFamily: 'Playfair Display, serif' }}>
                O premium fica vendavel quando o fornecedor enxerga distribuicao.
              </h2>
              <p className="mt-4 text-[#4B403D]">
                Por isso o Virei Noiva Fornecedores precisa parecer mais do que um cadastro: precisa parecer um lugar onde a marca do fornecedor fica mais bonita, mais confiavel e mais facil de ser descoberta.
              </p>
            </div>

            <div className="rounded-[1.5rem] border border-[#E8B7B1]/35 bg-[#FDF6F3] p-5">
              <p className="text-sm font-semibold text-[#1C1C1C]">O que o fornecedor premium esta comprando</p>
              <div className="mt-4 space-y-3 text-sm text-[#4B403D]">
                <p className="flex items-start gap-2"><Users className="mt-0.5 h-4 w-4 text-[#C8A97E]" />Mais chances de ser descoberto por casais.</p>
                <p className="flex items-start gap-2"><Sparkles className="mt-0.5 h-4 w-4 text-[#C8A97E]" />Mais destaque visual e mais percepcao de marca.</p>
                <p className="flex items-start gap-2"><TrendingUp className="mt-0.5 h-4 w-4 text-[#C8A97E]" />Mais distribuicao dentro da vitrine de fornecedores.</p>
              </div>
            </div>
          </div>
        </section>
      </main>
    </div>
  );
}
